import { useState } from 'react'
import { MessageCircle, Reply, Loader2 } from 'lucide-react'
import { addComment, type FeedbackComment } from '../../lib/feedbackApi'
import { formatRelativeTime } from '../../lib/format'

interface FeedbackCommentsProps {
  feedbackId: number
  comments: FeedbackComment[]
  onCommentAdded: () => void
}

export function FeedbackComments({
  feedbackId,
  comments,
  onCommentAdded,
}: FeedbackCommentsProps) {
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [replyTo, setReplyTo] = useState<{ id: number; author: string } | null>(null)
  const [replyText, setReplyText] = useState('')
  const [submittingReply, setSubmittingReply] = useState(false)

  async function handleSubmitComment(e: React.FormEvent) {
    e.preventDefault()
    const body = newComment.trim()
    if (!body || submitting) return

    setSubmitting(true)
    try {
      await addComment(feedbackId, body)
      setNewComment('')
      onCommentAdded()
    } catch {
      // Comment submission failed silently
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSubmitReply(e: React.FormEvent) {
    e.preventDefault()
    const body = replyText.trim()
    if (!body || !replyTo || submittingReply) return

    setSubmittingReply(true)
    try {
      await addComment(feedbackId, body, replyTo.id)
      setReplyText('')
      setReplyTo(null)
      onCommentAdded()
    } catch {
      // Reply submission failed silently
    } finally {
      setSubmittingReply(false)
    }
  }

  // Separate top-level comments (no parent_id) from replies
  const topLevel = comments.filter((c) => !c.parent_id)

  return (
    <div className="space-y-4">
      {topLevel.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <MessageCircle className="w-4 h-4" />
          <span>No comments yet. Be the first to share your thoughts.</span>
        </div>
      )}

      {topLevel.map((comment) => (
        <div key={comment.id} className="space-y-2">
          {/* Top-level comment */}
          <CommentBubble comment={comment}>
            <button
              onClick={() =>
                setReplyTo(
                  replyTo?.id === comment.id
                    ? null
                    : { id: comment.id, author: comment.author?.name || comment.author_name || 'User' }
                )
              }
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Reply className="w-3 h-3" />
              Reply
            </button>
          </CommentBubble>

          {/* Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="pl-8 border-l-2 border-border space-y-2">
              {comment.replies.map((reply) => (
                <CommentBubble key={reply.id} comment={reply} />
              ))}
            </div>
          )}

          {/* Inline reply form */}
          {replyTo?.id === comment.id && (
            <div className="pl-8">
              <form onSubmit={handleSubmitReply} className="space-y-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Reply to ${replyTo.author}...`}
                  rows={2}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={!replyText.trim() || submittingReply}
                    className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-1.5"
                  >
                    {submittingReply && <Loader2 className="w-3 h-3 animate-spin" />}
                    Reply
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReplyTo(null)
                      setReplyText('')
                    }}
                    className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-secondary transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      ))}

      {/* Add comment form */}
      <form onSubmit={handleSubmitComment} className="space-y-2 pt-2 border-t border-border">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          rows={3}
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-y"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Comment
          </button>
        </div>
      </form>
    </div>
  )
}

function CommentBubble({
  comment,
  children,
}: {
  comment: FeedbackComment
  children?: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium text-foreground">{comment.author?.name || comment.author_name || 'User'}</span>
        <span className="text-muted-foreground">
          {formatRelativeTime(comment.created_at)}
        </span>
      </div>
      <p className="text-sm text-foreground whitespace-pre-wrap">{comment.body}</p>
      {children}
    </div>
  )
}
